import { IErrorResponseData, INotification, IEnvelope } from '@iamo/notification';
import { IEnrichedTransactionParameters, IExportableTransactionParameters, ISignature, ITransactionParameters } from './Transaction';
import * as c from '@iamo/custody-lib';
import { IConnection } from './Connection';
import { IDataStorage } from '@iamo/utils';
import {
    Matrix
} from '@iamo/notification';

export { IErrorResponseData, INotification, IEnvelope };

/**
 * Notification Data types
 */
export type NotificationData =
    | INewTransactionMessageData
    | ISignRequestMessageData
    | ISignedMessageData
    | ITransactionSentMessageData
    | IOwnerInvitationMessageData
    | IOwnerInvitationResponseMessageData
    | IResponseToExternalInviteMessageData
    | IUpdateTransactionData
    | IErrorResponseData
    | IImportWalletData
    | IChallengedData
    | IOwnerRemovedData
    | ISignRequestRejectedMessageData;

export interface INewTransactionMessageData {
    txParameters: ITransactionParameters;
    name?: string;
    txHash: string;
    instanceAddress: string;
    sigs?: ISignature[];
    ethereumTxHash?: string;
    timestamp: number;
    description: string;
}

export type ProxyData = any;

export interface IPrivacyWalletData {
    multisigAddress: string;
    name: string;
    accountSeparationModuleAddress?: string;
    derivationParameters: {
        privacyWalletIndex: number;
        controllerInstance: string;
    };
    deploymentParameters: {
        nonce: number;
        salt: string;
    };
}
export interface IImportWalletData {
    timestamp: number;
    privacyWallets: IPrivacyWalletData[];
    controllerWalletAddress: string;
}

export interface IChallengedData {
    timestamp: number;
    challenger: string;
    nextOwner: string;
}

export interface IOwnerRemovedData {
    timestamp: number;
    walletAddress: string;
    ethereumTransactionHash: string;
}

export interface IUpdateTransactionData {
    timestamp: number;
    data: any;
}

export interface ISignRequestMessageData {
    txParameters: IExportableTransactionParameters;
    description?: string;
    timestamp: number;
}

export interface ISignedMessageData {
    txParameters: IExportableTransactionParameters;
    timestamp: number;
}

export interface ITransactionSentMessageData {
    txHash: string;
    instanceAddress: string;
    ethereumTxHash?: string;
    timestamp: number;
}

export interface IOwnerInvitationMessageData {
    txParameters: ITransactionParameters;
    name?: string;
    txHash: string;
    instanceAddress: string;
    sigs?: ISignature[];
    ethereumTxHash?: string;
    timestamp: number;
    description: string;
}

export interface IOwnerInvitationResponseMessageData {
    txParameters: ITransactionParameters;
    instanceAddress: string;
    agreed: boolean;
    txHash: string;
    timestamp: number;
    description?: string;
}

export interface IResponseToExternalInviteMessageData {
    address: string;
    instanceAddress: string;
    newAccountIdentityAddress: string;
    newAccountENSName: string;
    keyRights: string;
}
export interface IPrivacyWalletCreatedData {
    timestamp: number;
    privacyWallet: IPrivacyWalletData;
}

export interface ISendDerivedAddressesData {
    timestamp: number;
    addresses: string[];
}

export interface ISignRequestRejectedMessageData {
    txParameters: IExportableTransactionParameters;
    reason: string;
    timestamp: number;
}


export enum NotificationTypes {
    NewTransaction = 'NewTransaction',
    SignRequest = 'SignRequest',
    Signed = 'Signed',
    TransactionSent = 'TransactionSent',
    OwnerInvitation = 'OwnerInvitation',
    OwnerInvitationResponse = 'OwnerInvitationResponse',
    Error = 'Error',
    ResponseToExternalInvite = 'ResponseToExternalInvite',
    UpdateTransaction = 'UpdateTransaction',
    ImportWallet = 'ImportWallet',
    PrivacyWalletCreated = 'PrivacyWalletCreated',
    SendDerivedAddresses = 'SendDerivedAddresses',
    Challenged = 'Challenged',
    OwnerRemoved = 'OwnerRemoved',
    SignRequestRejected = 'SignRequestRejected',
}

const LOG = '[API/Notification]';

/**
 * Notification-class of the wallet
 */
export class Notification {

    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initializes the class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Create NewTransaction notification message body
     * @param txParameters The transaction-parameters
     * @param description The description
     * @returns The new INewTransactionMessageData-object
     */
    createNewTransactionMessageData(
        txParameters: IEnrichedTransactionParameters,
        description?: string
    ): INewTransactionMessageData {
        return c.Notification.createNewTransactionMessageData(txParameters, description)
    }

    /**
     * Creates the message data for the notification that informs an account that they
     * have been challenged.
     * @param challenger The account initiating the challenge
     * @param nextOwner The key that will replace the challenged key
     * @returns The new IChallengedData-object
     */
    createChallengeMessageData(
        challenger: string,
        nextOwner: string
    ): IChallengedData {
        return c.Notification.createChallengedMessageData(challenger, nextOwner)
    }

    /**
     * Creates the message data for the notification that informs the owner is removed
     * @param walletAddress The wallet address that sent owner removed from
     * @param ethereumTransactionHash The transaction hash that owner was removed
     * @returns The new IOwnerRemovedData-object
     */
    createOwnerRemovedMessageData(
        walletAddress: string,
        ethereumTransactionHash: string
    ): IOwnerRemovedData {
        return c.Notification.createOwnerRemovedMessageData(walletAddress, ethereumTransactionHash)
    }

    /**
     *  Create UpdateTransaction notification message body
     * @param data The data for updating the transaction
     * @returns The IUpdateTransactionData-object
     */
    createUpdateTransactionMessageData(data: any): IUpdateTransactionData {
        return c.Notification.createUpdateTransactionMessageData(data)
    }

    /**
     * Creates and formats ISendDerivedAddressesData to add to
     * sendDerivedAddress notification
     * @param addresses The list of derived addresses
     * @returns The ISendDerivedAddressesData-object
     */
    createDerivedAddressData(
        addresses: string[]
    ): ISendDerivedAddressesData {
        return c.Notification.createDerivedAddressData(addresses)
    }

    /**
     * Creates and formats the IPrivacyWalletCreatedData
     * @param privacyWallet The new Privacy Wallet
     * @returns The IPrivacyWalletCreatedData-object
     */
    createPrivacyWalletCreationData(
        privacyWallet: IPrivacyWalletData
    ): IPrivacyWalletCreatedData {
        return c.Notification.createPrivacyWalletCreationData(privacyWallet)
    }

    /**
     * Creates ImportWallet notification body
     * @param privacyWallets An object containing any privacy wallet addresses to import
     * @param controllerWalletAddress The controller wallet to import
     * @returns The IImportWalletData-object
     */
    createImportWalletData(
        privacyWallets: IPrivacyWalletData[],
        controllerWalletAddress: string,
    ): IImportWalletData {
        return c.Notification.createImportWalletData(privacyWallets, controllerWalletAddress)
    }

    /**
     * Create SignRequest notification message body
     * @param txParameters The transaction-parameters
     * @param description The description
     * @returns The ISignRequestMessageData-object
     */
    createSignRequestMessageData(
        txParameters: IExportableTransactionParameters,
        description?: string, 
    ): ISignRequestMessageData {
        return c.Notification.createSignRequestMessageData(txParameters, description)
    }

    /**
     *  Create Signed notification message body
     * @param txParameters The transaction-parameters
     * @returns The ISignedMessageData-object
     */
    createSignedMessageData(txParameters: IExportableTransactionParameters): ISignedMessageData {
        return c.Notification.createSignRequestMessageData(txParameters)
    }

    /**
     * Create SignRequestRejected notification message body
     * @param txParameters The transaction-parameters
     * @param reason The reason
     * @returns The ISignRequestRejectedMessageData-object
     */
    createSignRequestRejectedData(
        txParameters: IExportableTransactionParameters,
        reason: string,
    ): ISignRequestRejectedMessageData {
        return c.Notification.createSignRequestRejectedData(txParameters, reason)
    }

    /**
     * Create Sent notification message body
     * @param txParameters The transaction-parameters
     * @returns The ITransactionSentMessageData-object
     */
    createTransactionSentMessageData(
        txParameters: IEnrichedTransactionParameters 
    ): ITransactionSentMessageData {
        return c.Notification.createTransactionSentMessageData(txParameters)
    }

    /**
     * Creates external invite reponse data
     * @param address The new address to add
     * @param instanceAddress The address of the multisig
     * @param newAccountIdentityAddress The new owners identity address
     * @param newAccountENSName The new accounts ens name
     * @param keyRights The rights of the key
     * @returns The IResponseToExternalInviteMessageData-object
     */
    createExternalInviteResponseData(
        address: string,
        instanceAddress: string,
        newAccountIdentityAddress: string,
        newAccountENSName: string,
        keyRights: string,
    ): IResponseToExternalInviteMessageData {
        return c.Notification.createExternalInviteResponseData(address, instanceAddress, newAccountIdentityAddress, newAccountENSName, keyRights)
    }

    /**
     * Create OwnerInvitation notification message body
     * @param txParameters The transaction-parameters
     * @param description The description
     * @returns The IOwnerInvitationMessageData-object
     */
    createOwnerInvitationMessageData(
        txParameters: IEnrichedTransactionParameters,
        description: string
    ): IOwnerInvitationMessageData {
        return c.Notification.createOwnerInvitationMessageData(txParameters, description)
    }

    /**
     * Create OwnerInvitationResponse notification message body
     * @param invitationMessage The owner invitation message to answer
     * @param agreed The response of the invitation
     * @param description The description
     * @returns The IOwnerInvitationResponseMessageData-object
     */
    createOwnerInvitationResponseMessageData(
        invitationMessage: IOwnerInvitationMessageData,
        agreed: boolean,
        description?: string,
    ): IOwnerInvitationResponseMessageData {
        return c.Notification.createOwnerInvitationResponseMessageData(invitationMessage, agreed, description)
    }

    /**
     * Create Error notification message body
     * @param txHash The transaction-hash
     * @param description The description
     * @returns The IErrorResponseData
     */
    createErrorMessageData(txHash: string, description: string): IErrorResponseData {
        return c.Notification.createErrorMessageData(txHash, description)
    }

    /**
     *  Send a message to other user.
     * @param envelope The message data 
     * @param txnId The identifier of the message
     * @param accessTokenRecovery The function to be called when sync failed
     */
    async sendNotification(
        envelope: IEnvelope,
        txnId: string,
        accessTokenRecovery: (error: Error) => Promise<void>,
    ): Promise<void> {
         c.Notification.sendNotification(this.connection, envelope, txnId, accessTokenRecovery)
    }

    /**
     * Retrieve all message history
     * @param address The target address. if it's not given, the defaultAccountAddress will be used.
     * @returns The array with INotification
     */
    async retrieveAllNotifications(address?: string): Promise<INotification[]> {
        return c.Notification.retrieveAllNotifications(this.connection, address)
    }

    /**
     * Check if matrixId is address's matrix id.
     * @param matrixId The matrix id (@{address}:{serverURL})
     * @param address The address
     * @returns true if the address is a matrix user
     */
    isMatrixUser(matrixId: string, address: string): boolean {
        return c.Notification.isMatrixUser(matrixId, address)
    }

    /**
     * Converts a matrixID to an address
     * @param matrixId The matrix id (@{address}:{serverURL})
     * @returns The address
     */
    toAddress(matrixId: string): string {
        return c.Notification.toAddress(matrixId)
    }

    /**
     * Replaces the data storage
     * @param newStorage The new arbitrarily storage
     */
    setStorage(newStorage: IDataStorage): void {
        c.Notification.setStorage(newStorage)
    }

    /**
     * Get the access token for matrix.
     * @param address The user address (0x....)
     * @param password The password
     * @returns The IMatrixRequest-object
     */
    async getMatrixAccessToken(
        address: string,
        password: string,
    ): Promise<Matrix.IMatrixRequest> {
        return c.Notification.getMatrixAccessToken(this.connection, address, password)
    }

    /**
     * Checks if the owner already exists.
     * @param address The user address (0x....)
     * @returns true of the matrix user exists
     */
    async checkMatrixUserExist(
        address: string
    ): Promise<boolean> {
        return c.Notification.checkMatrixUserExist(this.connection, address)
    }

    /**
     * Creates the message data
     * @param to Where to send to
     * @param type The message type
     * @param txnHash The transaction-hash
     * @param data The data-body
     * @returns The IEnvelope-object
     */
    createEnvelope(
        to: string,
        type: NotificationTypes,
        txnHash: string,
        data: any,
    ): IEnvelope {
        return c.Notification.createEnvelope(this.connection, to, type, txnHash, data)
    }
}

export default Notification